import {
  CheckCircle,
  ChevronRight,
  CreditCard,
  Eye,
  EyeOff,
  Loader2,
  Paperclip,
  Search,
  User,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../lib/api";
import { INDIAN_STATES, SERVICES } from "../../data/services";
import { DEFAULT_DOCUMENTS, SERVICE_DOCUMENTS } from "../../data/serviceDocuments";

const PAYMENT_METHODS = [
  { value: "cash",          label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer / NEFT / RTGS" },
  { value: "upi",           label: "UPI" },
  { value: "cheque",        label: "Cheque" },
  { value: "razorpay",      label: "Razorpay (manual entry)" },
  { value: "manual",        label: "Other / Manual" },
];

const STEP_STATUS = {
  completed:     { label: "Completed",     text: "text-[#16A34A]", bg: "bg-[#DCFCE7]" },
  pending:       { label: "Pending",       text: "text-[#6C63FF]", bg: "bg-[#EDE9FE]" },
  admin_pending: { label: "Action Needed", text: "text-[#F97316]", bg: "bg-[#FFF7ED]" },
  not_started:   { label: "Not Started",   text: "text-[#9CA3AF]", bg: "bg-[#F3F4F6]" },
};

// ── Primitives ───────────────────────────────────────────────────────────────

const inputCls =
  "w-full bg-[#FAFAFD] border border-[#E8E6F8] rounded-xl px-3.5 py-2.5 text-sm text-[#1A1A2E] font-body focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/25 focus:border-[#6C63FF]/50 transition-colors placeholder:text-[#C4C0F8]";

function Field({ label, error, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-body font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5">
        {label}
      </label>
      {children}
      {error && <p className="mt-1.5 text-[11px] text-red-500 font-body">{error}</p>}
    </div>
  );
}

function ErrBox({ msg }) {
  if (!msg) return null;
  return (
    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 font-body">
      {msg}
    </div>
  );
}

function SectionCard({ number, title, subtitle, active, done, children }) {
  return (
    <div
      className={`rounded-2xl border transition-all ${
        done
          ? "border-[#BBF7D0] bg-white"
          : active
          ? "border-[#6C63FF]/30 bg-white shadow-sm"
          : "border-[#E8E6F8] bg-[#FAFAFD] opacity-50 pointer-events-none select-none"
      }`}
    >
      <div className={`flex items-center gap-3 px-5 py-4 border-b ${done ? "border-[#BBF7D0]" : "border-[#E8E6F8]"}`}>
        <div
          className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 transition-colors ${
            done ? "bg-[#16A34A] text-white" : active ? "bg-[#6C63FF] text-white" : "bg-[#E8E6F8] text-[#9CA3AF]"
          }`}
        >
          {done ? <CheckCircle className="h-4 w-4" /> : number}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-heading font-bold text-[#1A1A2E]">{title}</p>
          {subtitle && <p className="text-[11px] text-[#9CA3AF] font-body mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {(active || done) && <div className="p-5">{children}</div>}
    </div>
  );
}

// ── Existing User Search ─────────────────────────────────────────────────────

function UserSearchPanel({ onSelect }) {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery]     = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // Server paginates at 20/page by default and caps `limit` at 100. Without
    // paging we only saw page 1 and any customer past #20 was unreachable —
    // the client-side filter below would never find them. Loop through every
    // page so the search covers the full customer base.
    let cancelled = false;
    (async () => {
      try {
        const all = [];
        let page = 1;
        while (true) {
          const { data } = await API.get("/admin/users", { params: { page, limit: 100 } });
          // Endpoint sometimes returns the array directly (older shape) and
          // sometimes `{ users, total, page, pages }` — handle both.
          if (Array.isArray(data)) {
            all.push(...data);
            break;
          }
          all.push(...(data?.users || []));
          const pages = data?.pages || 1;
          if (page >= pages || !(data?.users || []).length) break;
          page += 1;
        }
        if (!cancelled) setUsers(all);
      } catch {
        /* ignore — surface as empty list */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sort alphabetically (A → Z, locale-aware so accented names sort naturally)
  // before filtering so the picker is easy to scan regardless of when a
  // customer was created.
  const filtered = users
    .filter(
      (u) =>
        !query ||
        u.name?.toLowerCase().includes(query.toLowerCase()) ||
        u.email?.toLowerCase().includes(query.toLowerCase()) ||
        String(u.phone || "").includes(query)
    )
    .slice()
    .sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
    );

  if (selected) {
    return (
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-[#6C63FF]/10 flex items-center justify-center text-[#6C63FF] text-sm font-black flex-shrink-0">
          {selected.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-heading font-bold text-[#1A1A2E]">{selected.name}</p>
          <p className="text-xs text-[#6B7280] font-body truncate">
            {selected.email} · {selected.phone}
          </p>
        </div>
        <button
          onClick={() => { setSelected(null); onSelect(null); }}
          className="text-xs text-[#9CA3AF] hover:text-red-400 font-body transition-colors"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={`${inputCls} pl-10`}
          placeholder="Search by name, email, or phone…"
        />
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-[#6C63FF]" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-sm text-[#9CA3AF] font-body py-6">
          {query ? "No users found" : "No customers yet"}
        </p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-0.5">
          {/* Render every match — the scrollable container caps the visible
              height, so a long customer list stays scannable without the UI
              hard-cutting the list at the first N rows. */}
          {filtered.map((user) => (
            <button
              key={user.id}
              onClick={() => { setSelected(user); onSelect(user); }}
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#E8E6F8] hover:border-[#6C63FF]/40 hover:bg-[#F5F4FB] text-left transition-all"
            >
              <div className="w-8 h-8 rounded-full bg-[#6C63FF]/10 flex items-center justify-center text-[#6C63FF] text-xs font-black flex-shrink-0">
                {user.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-body font-semibold text-[#1A1A2E] truncate">{user.name}</p>
                <p className="text-xs text-[#9CA3AF] font-body truncate">{user.email}</p>
              </div>
              <span className="text-[10px] text-[#9CA3AF] font-body flex-shrink-0">#{user.id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Single document field ─────────────────────────────────────────────────────

function DocField({ doc, file, text, showPass, onFile, onText, onTogglePass }) {
  const isReq = doc.required;
  return (
    <div className="bg-white border border-[#E8E6F8] rounded-xl p-3.5 space-y-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-body font-semibold text-[#1A1A2E]">{doc.label}</p>
          {doc.hint && <p className="text-[11px] text-[#9CA3AF] font-body mt-0.5">{doc.hint}</p>}
        </div>
        <span
          className={`text-[10px] font-body font-bold px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5 ${
            isReq
              ? "bg-red-50 text-red-500 border border-red-200"
              : "bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0]"
          }`}
        >
          {isReq ? "Required" : "Optional"}
        </span>
      </div>

      {/* File picker (for file and pan types) */}
      {(doc.type === "file" || doc.type === "pan") && (
        <div className="flex items-center gap-2">
          <label className="flex-1 flex items-center gap-2 border border-dashed border-[#D1D5DB] rounded-lg px-3 py-2 cursor-pointer hover:border-[#6C63FF]/50 hover:bg-[#FAFAFD] transition-colors">
            <Paperclip className="h-3.5 w-3.5 text-[#9CA3AF] flex-shrink-0" />
            <span className="text-xs font-body text-[#6B7280] flex-1 truncate">
              {file ? file.name : "Choose file…"}
            </span>
            <input type="file" className="hidden" onChange={(e) => onFile(e.target.files?.[0] || null)} />
          </label>
          {file && (
            <button onClick={() => onFile(null)} className="text-[#9CA3AF] hover:text-red-400 transition-colors">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Text input (for text, password, pan types) */}
      {(doc.type === "password" || doc.type === "text" || doc.type === "pan") && (
        <div className="relative">
          <input
            type={doc.type === "password" && !showPass ? "password" : "text"}
            value={text || ""}
            onChange={(e) => onText(e.target.value)}
            placeholder={doc.placeholder || `Enter ${doc.label.toLowerCase()}…`}
            className={inputCls}
          />
          {doc.type === "password" && (
            <button
              type="button"
              onClick={onTogglePass}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
            >
              {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Documents Step Form ───────────────────────────────────────────────────────

function DocumentsStepForm({ serviceId, orderId, stepNumber, onComplete }) {
  const allDocs = SERVICE_DOCUMENTS[serviceId] || DEFAULT_DOCUMENTS;
  const required = allDocs.filter((d) => d.required !== false && d.required !== undefined ? d.required : false);
  const optional = allDocs.filter((d) => !d.required);

  const [docFiles, setDocFiles] = useState({});
  const [docTexts, setDocTexts] = useState({});
  const [showPass, setShowPass] = useState({});
  const [loading, setLoading]   = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError]       = useState("");

  const setFile  = (key, f)   => setDocFiles((p) => ({ ...p, [key]: f || undefined }));
  const setText  = (key, val) => setDocTexts((p) => ({ ...p, [key]: val }));
  const toggleP  = (key)      => setShowPass((p) => ({ ...p, [key]: !p[key] }));

  const handleSubmit = async () => {
    setLoading(true); setError(""); setProgress("");
    try {
      // 1. Upload each file AS A CLIENT DOCUMENT (on the customer's behalf).
      // We hit /client-upload — not /upload — so each file lands with its
      // real customer document_type (pan_card, aadhar_card, bank_statements,
      // etc.) and shows up inside the Step 1 group in the Workflow tab,
      // exactly like a customer self-upload. The /upload endpoint would
      // instead prefix them with `admin_` and file them under "Admin
      // Uploaded" work-product, which is wrong for this flow.
      const fileEntries = Object.entries(docFiles).filter(([, f]) => f);
      for (let i = 0; i < fileEntries.length; i++) {
        const [key, file] = fileEntries[i];
        const docMeta = allDocs.find((d) => d.key === key);
        setProgress(`Uploading ${docMeta?.label || key} (${i + 1}/${fileEntries.length})…`);
        const fd = new FormData();
        fd.append("deliverable", file);
        fd.append("document_type", key);
        await API.post(
          `/admin/orders/${orderId}/steps/${stepNumber}/client-upload`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }
      // 2. Save text/password values as a note
      const textEntries = Object.entries(docTexts).filter(([, v]) => v?.trim());
      if (textEntries.length > 0) {
        setProgress("Saving text values…");
        const labelMap = allDocs.reduce((m, d) => ({ ...m, [d.key]: d.label }), {});
        const noteBody = textEntries
          .map(([k, v]) => `${labelMap[k] || k}: ${v}`)
          .join("\n");
        await API.post(`/admin/orders/${orderId}/notes`, {
          note: `[Document Info — uploaded by admin]\n${noteBody}`,
        });
      }
      // 3. Mark step complete
      setProgress("Marking Documents step complete…");
      await API.put(`/admin/orders/${orderId}/steps/${stepNumber}/complete`);
      setLoading(false); setProgress("");
      onComplete();
    } catch (err) {
      setLoading(false); setProgress("");
      setError(err?.response?.data?.detail || "Failed to complete Documents step");
    }
  };

  const hasAnyInput =
    Object.values(docFiles).some(Boolean) ||
    Object.values(docTexts).some((v) => v?.trim());

  return (
    <div className="space-y-4">
      {/* Required documents */}
      {required.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-body font-bold text-red-500 uppercase tracking-widest">
            Required — {required.length} document{required.length > 1 ? "s" : ""}
          </p>
          {required.map((doc) => (
            <DocField
              key={doc.key}
              doc={doc}
              file={docFiles[doc.key]}
              text={docTexts[doc.key]}
              showPass={showPass[doc.key]}
              onFile={(f) => setFile(doc.key, f)}
              onText={(v) => setText(doc.key, v)}
              onTogglePass={() => toggleP(doc.key)}
            />
          ))}
        </div>
      )}

      {/* Optional documents */}
      {optional.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-body font-bold text-[#9CA3AF] uppercase tracking-widest">
            Optional — {optional.length} document{optional.length > 1 ? "s" : ""}
          </p>
          {optional.map((doc) => (
            <DocField
              key={doc.key}
              doc={doc}
              file={docFiles[doc.key]}
              text={docTexts[doc.key]}
              showPass={showPass[doc.key]}
              onFile={(f) => setFile(doc.key, f)}
              onText={(v) => setText(doc.key, v)}
              onTogglePass={() => toggleP(doc.key)}
            />
          ))}
        </div>
      )}

      {progress && (
        <div className="flex items-center gap-2 text-sm text-[#6C63FF] font-body">
          <Loader2 className="h-4 w-4 animate-spin" />
          {progress}
        </div>
      )}
      <ErrBox msg={error} />

      <div className="flex items-center justify-between pt-1">
        <p className="text-xs text-[#9CA3AF] font-body">
          {hasAnyInput ? "Ready to submit" : "Upload at least one document or skip"}
        </p>
        <div className="flex gap-2">
          <button
            onClick={() =>
              API.put(`/admin/orders/${orderId}/steps/${stepNumber}/complete`)
                .then(onComplete)
                .catch((e) => setError(e?.response?.data?.detail || "Failed"))
            }
            disabled={loading}
            className="text-xs font-body text-[#9CA3AF] hover:text-[#6B7280] px-3 py-2 rounded-lg border border-[#E8E6F8] hover:border-[#D1D5DB] transition-colors disabled:opacity-50"
          >
            Skip & Mark Complete
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-2 bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-50 text-white text-sm font-body font-semibold px-4 py-2 rounded-xl transition-colors"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            {loading ? "Uploading…" : "Submit & Complete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Step Card ─────────────────────────────────────────────────────────────────

function StepCard({ step, index, serviceId, orderId, stepSt, setStepSt, onRefresh }) {
  const ss        = stepSt || {};
  const meta      = STEP_STATUS[step.status] || STEP_STATUS.not_started;
  const isPayment = step.name === "Payment";
  const isDocs    = step.name === "Documents" || step.name === "Upload Documents";
  const isDone    = step.status === "completed";
  const expanded  = ss.expanded || false;

  const set = (patch) => setStepSt(patch);

  const handleComplete = async () => {
    set({ loading: true, error: "" });
    try {
      if (ss.file) {
        const fd = new FormData();
        fd.append("deliverable", ss.file);
        await API.post(
          `/admin/orders/${orderId}/steps/${step.step_number}/upload`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        await API.put(`/admin/orders/${orderId}/steps/${step.step_number}/complete`);
      } else {
        await API.put(`/admin/orders/${orderId}/steps/${step.step_number}/complete`);
      }
      set({ loading: false, done: true, expanded: false, file: null });
      onRefresh();
    } catch (err) {
      set({ loading: false, error: err?.response?.data?.detail || "Action failed" });
    }
  };

  const handlePayment = async () => {
    if (!ss.payAmount) { set({ error: "Amount is required" }); return; }
    set({ loading: true, error: "" });
    try {
      await API.post(
        `/admin/orders/${orderId}/steps/${step.step_number}/mark-payment`,
        {
          amount:     Number(ss.payAmount),
          method:     ss.payMethod || "cash",
          payment_id: ss.payRef   || undefined,
          notes:      ss.payNotes || undefined,
        }
      );
      set({ loading: false, done: true, expanded: false });
      onRefresh();
    } catch (err) {
      set({ loading: false, error: err?.response?.data?.detail || "Payment failed" });
    }
  };

  return (
    <div
      className={`rounded-xl border transition-all ${
        isDone
          ? "border-[#BBF7D0] bg-[#F0FDF4]"
          : expanded
          ? "border-[#6C63FF]/30 bg-white shadow-sm"
          : "border-[#E8E6F8] bg-white"
      }`}
    >
      {/* Header row */}
      <div
        className={`flex items-center gap-3 px-4 py-3 ${!isDone ? "cursor-pointer select-none" : ""}`}
        onClick={() => !isDone && set({ expanded: !expanded })}
      >
        <div
          className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-black flex-shrink-0 ${
            isDone ? "bg-[#16A34A] text-white" : "bg-[#F5F4FB] border border-[#E8E6F8] text-[#6B7280]"
          }`}
        >
          {isDone ? <CheckCircle className="h-4 w-4" /> : index + 1}
        </div>
        <p className={`flex-1 text-sm font-body font-semibold ${isDone ? "text-[#16A34A]" : "text-[#1A1A2E]"}`}>
          {step.name}
        </p>
        <span className={`text-[10px] font-body font-semibold px-2.5 py-0.5 rounded-full ${meta.bg} ${meta.text}`}>
          {meta.label}
        </span>
        {!isDone && (
          <ChevronRight
            className={`h-4 w-4 text-[#9CA3AF] transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
          />
        )}
      </div>

      {/* Action panel */}
      {!isDone && expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-[#E8E6F8]">
          {isDocs ? (
            /* ── Documents step: show full document form */
            <DocumentsStepForm
              serviceId={serviceId}
              orderId={orderId}
              stepNumber={step.step_number}
              onComplete={() => { set({ expanded: false }); onRefresh(); }}
            />
          ) : isPayment ? (
            /* ── Payment step */
            <div className="space-y-3 pt-1">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Amount (₹) *">
                  <input
                    type="number"
                    value={ss.payAmount || ""}
                    onChange={(e) => set({ payAmount: e.target.value })}
                    className={inputCls}
                    placeholder="0"
                  />
                </Field>
                <Field label="Payment Method">
                  <select
                    value={ss.payMethod || "cash"}
                    onChange={(e) => set({ payMethod: e.target.value })}
                    className={inputCls}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Transaction / Ref ID (optional)">
                  <input
                    value={ss.payRef || ""}
                    onChange={(e) => set({ payRef: e.target.value })}
                    className={inputCls}
                    placeholder="UTR / Cheque no. / pay_XXXX"
                  />
                </Field>
                <Field label="Notes (optional)">
                  <input
                    value={ss.payNotes || ""}
                    onChange={(e) => set({ payNotes: e.target.value })}
                    className={inputCls}
                    placeholder="e.g. Collected at office"
                  />
                </Field>
              </div>
              <ErrBox msg={ss.error} />
              <div className="flex justify-end">
                <button
                  onClick={handlePayment}
                  disabled={ss.loading}
                  className="flex items-center gap-2 bg-[#16A34A] hover:bg-[#15803D] disabled:opacity-50 text-white text-sm font-body font-semibold px-4 py-2 rounded-xl transition-colors"
                >
                  {ss.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  {ss.loading ? "Saving…" : "Mark Payment Received"}
                </button>
              </div>
            </div>
          ) : (
            /* ── Generic step: optional file + mark complete */
            <div className="space-y-3 pt-1">
              <div className="flex items-center gap-2">
                <label className="flex-1 flex items-center gap-2 border border-dashed border-[#D1D5DB] rounded-xl px-3 py-2.5 cursor-pointer hover:border-[#6C63FF]/50 hover:bg-[#FAFAFD] transition-colors">
                  <Paperclip className="h-4 w-4 text-[#9CA3AF] flex-shrink-0" />
                  <span className="text-sm font-body text-[#6B7280] flex-1 truncate">
                    {ss.file ? ss.file.name : "Attach a file (optional)"}
                  </span>
                  <input type="file" className="hidden" onChange={(e) => set({ file: e.target.files?.[0] || null })} />
                </label>
                {ss.file && (
                  <button onClick={() => set({ file: null })} className="text-[#9CA3AF] hover:text-red-400 transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <ErrBox msg={ss.error} />
              <div className="flex justify-end">
                <button
                  onClick={handleComplete}
                  disabled={ss.loading}
                  className="flex items-center gap-2 bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-50 text-white text-sm font-body font-semibold px-4 py-2 rounded-xl transition-colors"
                >
                  {ss.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  {ss.loading ? "Saving…" : "Mark Complete"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CreateCustomerPage() {
  const navigate = useNavigate();

  // Mode: "new" | "existing"
  const [mode, setMode] = useState("new");

  // Section 1 — new customer form
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "",
    pincode: "", state: "", city: "", country: "India", company: "",
  });
  const [formErrors, setFormErrors]     = useState({});
  const [s1Error, setS1Error]           = useState("");
  const [s1Loading, setS1Loading]       = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const latestPincodeRef                = useRef("");

  // Shared: the resolved customer (either created or selected)
  const [customer, setCustomer] = useState(null);

  // Section 2 — service
  const [selectedSvcId, setSelectedSvcId] = useState("");
  const [createdOrder, setCreatedOrder]   = useState(null);
  const [orderDetail, setOrderDetail]     = useState(null);
  const [s2Loading, setS2Loading]         = useState(false);
  const [s2Error, setS2Error]             = useState("");

  // Section 3 — per-step expandable state
  const [stepStates, setStepStates] = useState({});

  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const patchStep = (num, patch) =>
    setStepStates((p) => ({ ...p, [num]: { ...p[num], ...patch } }));

  const handlePincodeChange = useCallback(
    async (pincode) => {
      setForm((p) => ({ ...p, pincode }));
      const cleaned = pincode.replace(/\s/g, "");
      latestPincodeRef.current = cleaned;
      if (cleaned.length < 3) return;
      const isStale = () => latestPincodeRef.current !== cleaned;

      if (/^\d{6}$/.test(cleaned)) {
        setPincodeLoading(true);
        try {
          const res = await fetch(`https://api.postalpincode.in/pincode/${cleaned}`);
          const data = await res.json();
          if (isStale()) return;
          if (data?.[0]?.Status === "Success" && data[0]?.PostOffice?.length > 0) {
            const po = data[0].PostOffice[0];
            setForm((f) => ({
              ...f,
              state:   po.State   || f.state,
              city:    po.District || f.city,
              country: po.Country  || "India",
            }));
          }
        } catch { /* leave fields untouched */ }
        finally { if (!isStale()) setPincodeLoading(false); }
        return;
      }

      if (cleaned.length >= 3 && cleaned.length <= 5) {
        const countryMap = {
          "United States": "us", "United Kingdom": "gb",
          Canada: "ca", Germany: "de", Australia: "au", France: "fr", India: "in",
        };
        const tryCountries =
          form.country && form.country !== "India" && countryMap[form.country]
            ? [countryMap[form.country]]
            : ["us", "gb", "ca", "de", "au"];
        setPincodeLoading(true);
        for (const cc of tryCountries) {
          if (isStale()) break;
          try {
            const res = await fetch(`https://api.zippopotam.us/${cc}/${cleaned}`);
            if (isStale()) break;
            if (res.ok) {
              const data = await res.json();
              if (isStale()) break;
              if (data?.places?.length > 0) {
                const place = data.places[0];
                setForm((f) => ({
                  ...f,
                  state:   place.state          || f.state,
                  city:    place["place name"]   || f.city,
                  country: data.country          || f.country,
                }));
                break;
              }
            }
          } catch { /* try next */ }
        }
        if (!isStale()) setPincodeLoading(false);
      }
    },
    [form.country],
  );

  // Reset order/steps when mode changes
  const switchMode = (m) => {
    setMode(m);
    setCustomer(null);
    setCreatedOrder(null);
    setOrderDetail(null);
    setStepStates({});
    setSelectedSvcId("");
    setForm({ name: "", email: "", phone: "", password: "", pincode: "", state: "", city: "", country: "India", company: "" });
    setFormErrors({});
    setS1Error("");
    setS2Error("");
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = () => {
    const e = {};
    if (!form.name.trim())  e.name     = "Required";
    if (!form.email.trim()) e.email    = "Required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Invalid email";
    if (!form.phone.trim()) e.phone    = "Required";
    if (!form.password || form.password.length < 8) e.password = "Min 8 characters";
    if (!form.pincode.trim()) e.pincode = "Required";
    if (!form.state.trim())  e.state   = "Required";
    if (!form.city.trim())   e.city    = "Required";
    return e;
  };

  // ── API calls ──────────────────────────────────────────────────────────────

  const handleCreateUser = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    setFormErrors({}); setS1Error(""); setS1Loading(true);
    try {
      const { data } = await API.post("/admin/users", form);
      setCustomer(data);
    } catch (err) {
      setS1Error(err?.response?.data?.detail || "Failed to create account");
    }
    setS1Loading(false);
  };

  const fetchOrderDetail = useCallback(async (orderId) => {
    try {
      const { data } = await API.get(`/admin/orders/${orderId}`);
      setOrderDetail(data);
    } catch {}
  }, []);

  const handleCreateOrder = async () => {
    if (!selectedSvcId) { setS2Error("Select a service first"); return; }
    setS2Error(""); setS2Loading(true);
    try {
      const { data } = await API.post(`/admin/users/${customer.id}/orders`, {
        service_id: selectedSvcId,
      });
      setCreatedOrder(data);
      await fetchOrderDetail(data.id);
    } catch (err) {
      setS2Error(err?.response?.data?.detail || "Failed to start service");
    }
    setS2Loading(false);
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const s1Done       = !!customer;
  const s2Done       = !!createdOrder;
  const selectedSvc  = SERVICES.find((s) => s.id === selectedSvcId);
  const steps        = orderDetail?.steps || [];
  const completedCnt = steps.filter((s) => s.status === "completed").length;
  const isNewMode    = mode === "new";

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-12">
      {/* Page heading */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-[#6C63FF]/10 flex items-center justify-center flex-shrink-0">
          <UserPlus className="h-5 w-5 text-[#6C63FF]" />
        </div>
        <div>
          <h1 className="text-xl font-heading font-bold text-[#1A1A2E]">Add Customer / Service</h1>
          <p className="text-xs text-[#9CA3AF] font-body">
            Create or find a customer → add service → complete workflow steps
          </p>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="flex bg-[#F5F4FB] rounded-xl p-1 gap-1">
        {[
          { id: "new",      icon: UserPlus, label: "New Customer" },
          { id: "existing", icon: Users,    label: "Existing Customer" },
        ].map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            onClick={() => switchMode(id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-body font-semibold transition-all ${
              mode === id
                ? "bg-white text-[#6C63FF] shadow-sm"
                : "text-[#9CA3AF] hover:text-[#6B7280]"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ─── Section 1: Customer ──────────────────────────────────────────── */}
      <SectionCard
        number="1"
        title={isNewMode ? "Customer Details" : "Find Customer"}
        subtitle={
          isNewMode
            ? "Create login credentials for the customer app"
            : "Search and select an existing customer"
        }
        active={!s1Done}
        done={s1Done}
      >
        {s1Done ? (
          /* Summary */
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#6C63FF]/10 flex items-center justify-center flex-shrink-0">
              <User className="h-5 w-5 text-[#6C63FF]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-heading font-bold text-[#1A1A2E]">{customer.name}</p>
              <p className="text-xs text-[#6B7280] font-body truncate">
                {customer.email} · {customer.phone}
              </p>
            </div>
            <span className="text-[10px] font-body font-bold text-[#16A34A] bg-[#DCFCE7] border border-[#BBF7D0] px-2.5 py-1 rounded-full flex-shrink-0">
              ID #{customer.id}
            </span>
          </div>
        ) : isNewMode ? (
          /* New customer form */
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label={<>Full Name <span className="text-red-500">*</span></>} error={formErrors.name}>
                <input value={form.name} onChange={setF("name")} className={inputCls} placeholder="Rajesh Kumar" />
              </Field>
              <Field label={<>Email <span className="text-red-500">*</span></>} error={formErrors.email}>
                <input type="email" value={form.email} onChange={setF("email")} className={inputCls} placeholder="rajesh@gmail.com" />
              </Field>
              <Field label={<>Phone <span className="text-red-500">*</span></>} error={formErrors.phone}>
                <input value={form.phone} onChange={setF("phone")} className={inputCls} placeholder="+91 98765 43210" />
              </Field>
              <Field label={<>Password <span className="text-red-500">*</span> <span className="normal-case tracking-normal font-normal">(min 8 chars)</span></>} error={formErrors.password}>
                <input type="password" value={form.password} onChange={setF("password")} className={inputCls} placeholder="••••••••" />
              </Field>
              <Field
                label={
                  <span className="flex items-center gap-2">
                    Pincode / Zip Code <span className="text-red-500">*</span>
                    {pincodeLoading && <Loader2 className="h-3 w-3 animate-spin text-[#6C63FF]" />}
                    {form.state && form.city && !pincodeLoading && form.pincode.length >= 3 && (
                      <span className="text-[10px] text-[#16A34A] font-body normal-case tracking-normal">Auto-filled</span>
                    )}
                  </span>
                }
                error={formErrors.pincode}
                className="col-span-2"
              >
                <input
                  value={form.pincode}
                  onChange={(e) => handlePincodeChange(e.target.value.replace(/[^\w\s-]/g, "").slice(0, 10))}
                  maxLength={10}
                  className={inputCls}
                  placeholder="Enter pincode to auto-fill state & city"
                />
              </Field>
              <Field label={<>State <span className="text-red-500">*</span></>} error={formErrors.state}>
                <input value={form.state} onChange={setF("state")} className={inputCls} placeholder="Maharashtra" />
              </Field>
              <Field label={<>City <span className="text-red-500">*</span></>} error={formErrors.city}>
                <input value={form.city} onChange={setF("city")} className={inputCls} placeholder="Mumbai" />
              </Field>
              <Field label="Company (optional)" className="col-span-2">
                <input value={form.company} onChange={setF("company")} className={inputCls} placeholder="Acme Pvt Ltd" />
              </Field>
            </div>
            <ErrBox msg={s1Error} />
            <div className="flex justify-end">
              <button
                onClick={handleCreateUser}
                disabled={s1Loading}
                className="flex items-center gap-2 bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-50 text-white text-sm font-body font-semibold px-5 py-2.5 rounded-xl transition-colors"
              >
                {s1Loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
                {s1Loading ? "Creating…" : "Create Account"}
              </button>
            </div>
          </div>
        ) : (
          /* Existing user search */
          <UserSearchPanel onSelect={(u) => setCustomer(u)} />
        )}
      </SectionCard>

      {/* ─── Section 2: Service ───────────────────────────────────────────── */}
      <SectionCard
        number="2"
        title="Select Service"
        subtitle="Choose the service for this customer"
        active={s1Done && !s2Done}
        done={s2Done}
      >
        {s2Done ? (
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-heading font-bold text-[#1A1A2E]">{createdOrder.service_name}</p>
              <p className="text-xs text-[#6B7280] font-body">Order #{createdOrder.id} · {steps.length} steps</p>
            </div>
            <span className="text-[10px] font-body font-bold text-[#16A34A] bg-[#DCFCE7] border border-[#BBF7D0] px-2.5 py-1 rounded-full">
              Created
            </span>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Service list */}
            <div className="space-y-2 max-h-56 overflow-y-auto pr-0.5">
              {SERVICES.map((svc) => (
                <label
                  key={svc.id}
                  className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                    selectedSvcId === svc.id
                      ? "border-[#6C63FF] bg-[#6C63FF]/5"
                      : "border-[#E8E6F8] hover:border-[#6C63FF]/40 hover:bg-[#FAFAFD]"
                  }`}
                >
                  <input
                    type="radio"
                    name="service"
                    value={svc.id}
                    checked={selectedSvcId === svc.id}
                    onChange={() => setSelectedSvcId(svc.id)}
                    className="accent-[#6C63FF] mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-body font-semibold text-[#1A1A2E]">{svc.name}</p>
                    <p className="text-xs text-[#9CA3AF] font-body">{svc.price}</p>
                  </div>
                </label>
              ))}
            </div>

            {/* Workflow step preview when service selected */}
            {selectedSvc?.workflowSteps && (
              <div className="rounded-xl border border-[#E8E6F8] bg-[#FAFAFD] p-4">
                <p className="text-[10px] font-body font-bold text-[#9CA3AF] uppercase tracking-widest mb-3">
                  {selectedSvc.workflowSteps.length} Workflow Steps
                </p>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                  {selectedSvc.workflowSteps.map((stepName, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-white border border-[#E8E6F8] flex items-center justify-center text-[9px] font-black text-[#9CA3AF] flex-shrink-0">
                        {i + 1}
                      </div>
                      <span className="text-xs font-body text-[#6B7280] truncate">{stepName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ErrBox msg={s2Error} />
            <div className="flex justify-end">
              <button
                onClick={handleCreateOrder}
                disabled={s2Loading || !selectedSvcId}
                className="flex items-center gap-2 bg-[#6C63FF] hover:bg-[#5B52CC] disabled:opacity-50 text-white text-sm font-body font-semibold px-5 py-2.5 rounded-xl transition-colors"
              >
                {s2Loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
                {s2Loading ? "Creating…" : "Start Service"}
              </button>
            </div>
          </div>
        )}
      </SectionCard>

      {/* ─── Section 3: Workflow Steps ────────────────────────────────────── */}
      {s2Done && steps.length > 0 && (
        <div className="rounded-2xl border border-[#E8E6F8] bg-white overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E8E6F8]">
            <div className="w-7 h-7 rounded-full bg-[#6C63FF] flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-black">3</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-heading font-bold text-[#1A1A2E]">Workflow Steps</p>
              <p className="text-[11px] text-[#9CA3AF] font-body">
                Complete each step on behalf of the customer — tap a step to expand it
              </p>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className="text-xs font-heading font-bold text-[#6C63FF]">
                {completedCnt}/{steps.length}
              </div>
              <div className="w-16 h-1.5 bg-[#E8E6F8] rounded-full mt-1 overflow-hidden">
                <div
                  className="h-full bg-[#6C63FF] rounded-full transition-all duration-500"
                  style={{ width: `${steps.length ? (completedCnt / steps.length) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
          <div className="p-4 space-y-2">
            {steps.map((step, i) => (
              <StepCard
                key={step.step_number}
                step={step}
                index={i}
                serviceId={selectedSvcId || createdOrder?.service_id}
                orderId={createdOrder.id}
                stepSt={stepStates[step.step_number]}
                setStepSt={(patch) => patchStep(step.step_number, patch)}
                onRefresh={() => fetchOrderDetail(createdOrder.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ─── Credentials / Summary card ───────────────────────────────────── */}
      {s2Done && (
        <div className="bg-gradient-to-br from-[#1A1A2E] via-[#2A1F5C] to-[#4F46E5] rounded-2xl p-6 text-white">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="h-5 w-5 text-[#86EFAC] flex-shrink-0" />
            <h3 className="font-heading font-bold text-base">
              {isNewMode ? "Customer Account Created" : "Service Added Successfully"}
            </h3>
          </div>
          <p className="text-[#A5B4FC] text-xs font-body mb-5 ml-7">
            {isNewMode
              ? <>Share credentials — customer signs in at <span className="text-white font-semibold">myca.pro/sign-in</span></>
              : "Service started and workflow is in progress."
            }
          </p>

          <div className="bg-white/10 border border-white/15 rounded-xl px-4 py-3.5 space-y-2 mb-5 text-sm font-body">
            <div className="flex items-center gap-3">
              <span className="text-[#A5B4FC] text-[11px] uppercase tracking-wider w-16 flex-shrink-0">Customer</span>
              <span className="font-semibold text-white">{customer?.name}</span>
            </div>
            {isNewMode && (
              <>
                <div className="flex items-center gap-3">
                  <span className="text-[#A5B4FC] text-[11px] uppercase tracking-wider w-16 flex-shrink-0">Email</span>
                  <span className="font-semibold text-white">{customer?.email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#A5B4FC] text-[11px] uppercase tracking-wider w-16 flex-shrink-0">Password</span>
                  <span className="font-mono font-bold text-white tracking-wide">{form.password}</span>
                </div>
              </>
            )}
            <div className="flex items-center gap-3">
              <span className="text-[#A5B4FC] text-[11px] uppercase tracking-wider w-16 flex-shrink-0">Service</span>
              <span className="font-semibold text-white">{createdOrder?.service_name}</span>
            </div>
            {completedCnt > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-[#A5B4FC] text-[11px] uppercase tracking-wider w-16 flex-shrink-0">Progress</span>
                <span className="text-[#86EFAC] font-semibold">
                  {completedCnt} of {steps.length} steps completed
                </span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate(`/admin/users/${customer?.id}`)}
              className="bg-white/15 hover:bg-white/25 text-white text-sm font-body font-semibold py-2.5 rounded-xl transition-colors"
            >
              View Profile
            </button>
            <button
              onClick={() => navigate(`/admin/orders/${createdOrder?.id}`)}
              className="bg-white text-[#4F46E5] hover:bg-[#EDE9FE] text-sm font-body font-semibold py-2.5 rounded-xl transition-colors"
            >
              View Full Order
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
